/**
 * Department admin panel (docs/guardian/03 §1.3/§1.5, 2026-10-21): invite by
 * email, revoke pending invites, change a member's role, suspend/reactivate,
 * remove. The Backend enforces admin role, seats and the last-admin rule;
 * this panel only shows its answers.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {
  createDepartmentInvites,
  fetchDepartmentInvites,
  fetchDepartmentMembers,
  removeDepartmentMember,
  revokeDepartmentInvite,
  updateDepartmentMember,
} from '../api/guardianApi';
import { departmentErrorMessage } from '../utils/departmentErrors';

const MAX_EMAILS = 50;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Splits pasted text on commas, semicolons, whitespace; de-duplicates. */
export function parseEmails(raw) {
  const all = String(raw || '')
    .split(/[\s,;]+/)
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
  const unique = [...new Set(all)];
  return {
    valid: unique.filter((e) => EMAIL_RE.test(e)),
    invalid: unique.filter((e) => !EMAIL_RE.test(e)),
  };
}

const INVITE_STATUS_TEXT = {
  sent: 'Invitation sent',
  already_member: 'Already a member',
  email_failed: 'Email could not be sent; try again',
};

function fullName(m) {
  const name = [m.firstName, m.lastName].filter(Boolean).join(' ').trim();
  return name || m.email;
}

/**
 * @param {object} props
 * @param {string} props.authToken
 * @param {() => void} props.onChanged - Refreshes seat counts in the parent.
 */
export default function DepartmentAdmin({ authToken, onChanged }) {
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [error, setError] = useState(null);
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState('member');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [m, i] = await Promise.all([
      fetchDepartmentMembers(authToken),
      fetchDepartmentInvites(authToken),
    ]);
    if (m?.success) setMembers(Array.isArray(m.data?.members) ? m.data.members : []);
    if (i?.success) setInvites(Array.isArray(i.data?.invites) ? i.data.invites : []);
    if (!m?.success || !i?.success) {
      setError(departmentErrorMessage(m?.success ? i : m, 'Could not load members and invitations.'));
    }
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (call, fallback) => {
    setError(null);
    setBusy(true);
    try {
      const res = await call();
      if (!res?.success) {
        setError(departmentErrorMessage(res, fallback));
        return false;
      }
      await load();
      onChanged?.();
      return true;
    } finally {
      setBusy(false);
    }
  };

  const { valid, invalid } = parseEmails(emails);
  const tooMany = valid.length > MAX_EMAILS;

  const sendInvites = async () => {
    setResults([]);
    setError(null);
    setBusy(true);
    try {
      const res = await createDepartmentInvites(authToken, valid, role);
      if (!res?.success) {
        setError(departmentErrorMessage(res, 'Could not send invitations.'));
        return;
      }
      setResults(Array.isArray(res.data?.results) ? res.data.results : []);
      setEmails('');
      await load();
    } finally {
      setBusy(false);
    }
  };

  const pending = invites.filter((i) => i.status === 'pending');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="h6" component="h2">Invite people</Typography>
        <TextField
          label="Email addresses"
          helperText={
            invalid.length
              ? `Not an email address: ${invalid.slice(0, 3).join(', ')}${invalid.length > 3 ? '…' : ''}`
              : `Separate with commas or new lines. Up to ${MAX_EMAILS} at a time.`
          }
          error={invalid.length > 0 || tooMany}
          multiline
          minRows={2}
          value={emails}
          onChange={(e) => setEmails(e.target.value)}
        />
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Select size="small" value={role} onChange={(e) => setRole(e.target.value)} inputProps={{ 'aria-label': 'Role for new members' }}>
            <MenuItem value="member">Member</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </Select>
          <Button
            variant="contained"
            onClick={sendInvites}
            disabled={busy || valid.length === 0 || invalid.length > 0 || tooMany}
          >
            Send invitations
          </Button>
        </Box>
        {results.length > 0 && (
          <Box component="ul" sx={{ m: 0, pl: 2 }} aria-live="polite">
            {results.map((r) => (
              <li key={r.email}>
                <Typography variant="body2">
                  {r.email}: {INVITE_STATUS_TEXT[r.status] || r.status}
                </Typography>
              </li>
            ))}
          </Box>
        )}
      </Paper>

      {pending.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" component="h2" sx={{ mb: 1 }}>Pending invitations</Typography>
          <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {pending.map((i) => (
              <Box component="li" key={i.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  {i.email} · {i.role === 'admin' ? 'Admin' : 'Member'} · expires {new Date(i.expiresAt).toLocaleDateString()}
                </Typography>
                <Button size="small" disabled={busy} onClick={() => act(() => revokeDepartmentInvite(authToken, i.id), 'Could not revoke the invitation.')}>
                  Revoke
                </Button>
              </Box>
            ))}
          </Box>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" component="h2" sx={{ mb: 1 }}>Members</Typography>
        <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {members.map((m) => (
            <Box component="li" key={m.id} sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, justifyContent: 'space-between' }}>
              <Box sx={{ minWidth: 200 }}>
                <Typography variant="body2">{fullName(m)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {m.email}{m.status === 'suspended' ? ' · Suspended' : ''}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Select
                  size="small"
                  value={m.role}
                  disabled={busy}
                  inputProps={{ 'aria-label': `Role for ${fullName(m)}` }}
                  onChange={(e) => act(() => updateDepartmentMember(authToken, m.id, { role: e.target.value }), 'Could not change the role.')}
                >
                  <MenuItem value="member">Member</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
                <Button
                  size="small"
                  disabled={busy}
                  onClick={() => act(
                    () => updateDepartmentMember(authToken, m.id, { status: m.status === 'active' ? 'suspended' : 'active' }),
                    'Could not change the member.',
                  )}
                >
                  {m.status === 'active' ? 'Suspend' : 'Reactivate'}
                </Button>
                <Button
                  size="small"
                  color="error"
                  disabled={busy}
                  onClick={() => act(() => removeDepartmentMember(authToken, m.id), 'Could not remove the member.')}
                >
                  Remove
                </Button>
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
}
